import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { isForbiddenMessage } from '../../lib/error-message';
import { cn } from '../../lib/utils';

/**
 * Mỗi màn hình dữ liệu phải xử lý đủ **bốn** trạng thái: loading, empty,
 * error, có dữ liệu (docs/frontend-guide.md §7). Ba cái đầu ở đây để không màn
 * hình nào phải tự nghĩ lại.
 */

/**
 * Skeleton giống hình dạng nội dung thật, **không** phải spinner giữa màn hình.
 * Spinner không cho biết sắp thấy gì, nên mỗi lần tải cảm giác như lần đầu.
 */
export function Skeleton({ className }: { className?: string }): ReactNode {
  return <div className={cn('animate-pulse rounded-DEFAULT bg-muted', className)} />;
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  /** Nói rõ **vì sao** trống và bước tiếp theo, đừng chỉ ghi "Không có dữ liệu". */
  description?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-DEFAULT border border-dashed border-border px-6 py-12 text-center">
      <p className="font-medium">{title}</p>
      {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}): ReactNode {
  const { t } = useTranslation();

  // 403 chung: ẩn hẳn, không nói gì — xem `isForbiddenMessage`.
  if (isForbiddenMessage(message, t)) return null;

  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center gap-3 rounded-DEFAULT border border-destructive/30 bg-destructive/5 px-6 py-10 text-center"
    >
      {/* Thông báo đọc được, KHÔNG phải stack trace. */}
      <p className="text-sm">{message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}
